<script setup lang="ts">
import { inject } from 'vue'
import type { IntakeDocument, IntakeDocumentSlot } from '@/api/schemas'
import { documentHrefKey } from './documentLinks'
import { slotFiles } from './formDefinitions'
import { uploadStatusKey } from './uploadStatus'

const props = defineProps<{
  slots: IntakeDocumentSlot[]
  documents: IntakeDocument[]
  /** Files under a kind no slot takes any more (e.g. a retired owner photo ID). Listed so they can still be removed. */
  others?: IntakeDocument[]
}>()

const emit = defineEmits<{
  upload: [kind: string, event: Event]
  remove: [id: number]
}>()

const documentHref = inject(documentHrefKey, undefined)
const status = inject(uploadStatusKey, undefined)

function uploading(slot: IntakeDocumentSlot) {
  return (status?.uploading.get(slot.kind) ?? 0) > 0
}
function removing(id: number) {
  return status?.removing.has(id) === true
}
// A failed upload shows at its slot's picker; a failed removal at the slot listing that file.
function failureIn(files: IntakeDocument[], kind?: string) {
  const failure = status?.failure
  if (!failure) return undefined
  const here =
    (kind !== undefined && failure.kind === kind) ||
    files.some((document) => document.id === failure.documentId)
  return here ? failure.message : undefined
}
function slotFailure(slot: IntakeDocumentSlot) {
  return failureIn(slotFiles(slot, props.documents), slot.kind)
}

const needLabels: Record<IntakeDocumentSlot['need'], string> = {
  required: 'Required',
  recommended: 'Recommended',
  if_applicable: 'If it applies',
}
</script>

<template>
  <section class="uploads">
    <h2>Documents</h2>
    <article
      v-for="slot in slots"
      :key="slot.kind"
      class="upload-slot"
      :aria-busy="uploading(slot)"
    >
      <header>
        <h3>{{ slot.label }}</h3>
        <span class="need-tag" :class="`need-${slot.need}`">{{ needLabels[slot.need] }}</span>
      </header>
      <p>
        {{ slot.help }}
        <a v-if="slot.source_url" :href="slot.source_url" target="_blank" rel="noopener noreferrer"
          >Google's guidance</a
        >
      </p>
      <ul v-if="slotFiles(slot, documents).length" class="uploaded-files">
        <li
          v-for="document in slotFiles(slot, documents)"
          :key="document.id"
          :class="{ 'just-added': status?.justAdded === document.id }"
        >
          <a
            v-if="documentHref"
            :href="documentHref(document.id)"
            target="_blank"
            rel="noopener"
            class="file-link"
            >{{ document.filename }}<span class="sr-only"> (opens in a new tab)</span></a
          >
          <span v-else>{{ document.filename }}</span>
          <span v-if="status?.justAdded === document.id" class="added-tag" aria-hidden="true"
            >Added</span
          >
          <button
            type="button"
            class="text-button"
            :aria-label="`Remove ${document.filename}`"
            :aria-disabled="removing(document.id)"
            @click="emit('remove', document.id)"
          >
            {{ removing(document.id) ? 'Removing…' : 'Remove' }}
          </button>
        </li>
      </ul>
      <!-- The native control always reads "No file chosen" once a page loads, even
           with files uploaded, so it stays hidden behind a button-styled label. -->
      <!-- Stays enabled while uploading: disabling it would drop the focus it holds. -->
      <label class="file-picker" :class="{ busy: uploading(slot) }">
        <input
          type="file"
          class="sr-only"
          accept="application/pdf,image/jpeg,image/png,image/heic,.heic"
          :aria-label="`Add a file: ${slot.label}`"
          @change="emit('upload', slot.kind, $event)"
        />
        <span aria-hidden="true">{{
          uploading(slot)
            ? 'Uploading…'
            : slotFiles(slot, documents).length
              ? 'Add another file'
              : 'Choose a file'
        }}</span>
      </label>
      <p v-if="slotFailure(slot)" class="slot-error" role="alert">{{ slotFailure(slot) }}</p>
    </article>
    <article v-if="others?.length" class="upload-slot">
      <header><h3>Other files you uploaded earlier</h3></header>
      <p>Google doesn't ask for these. Remove any you don't want us to keep with your intake.</p>
      <ul class="uploaded-files">
        <li v-for="document in others" :key="document.id">
          <a
            v-if="documentHref"
            :href="documentHref(document.id)"
            target="_blank"
            rel="noopener"
            class="file-link"
            >{{ document.filename }}<span class="sr-only"> (opens in a new tab)</span></a
          >
          <span v-else>{{ document.filename }}</span>
          <span v-if="status?.justAdded === document.id" class="added-tag" aria-hidden="true"
            >Added</span
          >
          <button
            type="button"
            class="text-button"
            :aria-label="`Remove ${document.filename}`"
            :aria-disabled="removing(document.id)"
            @click="emit('remove', document.id)"
          >
            {{ removing(document.id) ? 'Removing…' : 'Remove' }}
          </button>
        </li>
      </ul>
      <p v-if="failureIn(others)" class="slot-error" role="alert">{{ failureIn(others) }}</p>
    </article>
    <p>PDF, JPEG, PNG, or HEIC; up to 20 MiB.</p>
  </section>
</template>

<style scoped>
.uploads {
  border-top: 1px solid var(--line);
  margin-top: 0.5rem;
  padding-top: 1rem;
  display: grid;
  gap: 1rem;
}
.uploads h2 {
  font-size: 1rem;
  margin: 0;
}
.upload-slot {
  display: grid;
  gap: 0.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e6df;
}
.upload-slot:last-of-type {
  border-bottom: 0;
  padding-bottom: 0;
}
.upload-slot header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}
.upload-slot h3 {
  font-size: 0.93rem;
  font-weight: 650;
  margin: 0;
}
.upload-slot p {
  font-size: 0.85rem;
  color: #526057;
  margin: 0;
  line-height: 1.45;
}
.need-tag {
  flex: none;
  font-size: 0.75rem;
  font-weight: 650;
  padding: 0.15rem 0.5rem;
  border-radius: 3px;
  background: #e7ecdf;
  color: #3b4a3d;
  white-space: nowrap;
}
.need-tag.need-required {
  background: #f0e0dc;
  color: #7a3320;
}
.need-tag.need-recommended {
  background: #f0f3dd;
  color: #5c6b1f;
}
.uploaded-files {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.35rem;
}
.uploaded-files li {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  margin: 0 -0.4rem;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.85rem;
}
.uploaded-files li .text-button {
  margin-left: auto;
}
.uploaded-files li.just-added {
  animation: just-added 2.5s ease-out;
}
@keyframes just-added {
  0%,
  40% {
    background: #e6edc6;
  }
  100% {
    background: transparent;
  }
}
.added-tag {
  flex: none;
  font-size: 0.75rem;
  font-weight: 650;
  color: #3d5a2a;
}
.added-tag::before {
  content: '✓ ';
}
.uploaded-files li > span,
.file-link {
  word-break: break-all;
}
.file-link {
  color: var(--pine);
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
.file-picker {
  position: relative;
  justify-self: start;
}
.file-picker span {
  display: inline-block;
  padding: 0.5rem 0.9rem;
  border: 1px solid var(--pine);
  border-radius: 3px;
  background: #fff;
  color: var(--pine);
  font-size: 0.9rem;
  font-weight: 650;
  cursor: pointer;
}
.file-picker:hover span {
  background: #f0f3ea;
}
.file-picker.busy span {
  cursor: progress;
  border-style: dashed;
  background: #f0f3ea;
}
.file-picker:focus-within span {
  outline: 3px solid var(--lime);
  outline-offset: 2px;
}
.text-button {
  align-self: start;
  width: fit-content;
  padding: 0;
  border: 0;
  background: none;
  color: var(--pine);
  font: inherit;
  font-weight: 650;
  text-decoration: underline;
  cursor: pointer;
}
.text-button[aria-disabled='true'] {
  cursor: progress;
  opacity: 0.7;
}
.slot-error {
  background: #fbe8e5;
  border-left: 4px solid #a53c28;
  padding: 0.5rem 0.7rem;
}
.upload-slot p.slot-error {
  color: #6e2618;
}
@media (prefers-reduced-motion: reduce) {
  .uploaded-files li.just-added {
    animation: none;
    background: #e6edc6;
  }
}
.uploads > p {
  font-size: 0.8rem;
  color: #526057;
  font-weight: 400;
  margin: 0;
}
</style>
